import React, {
  ChangeEvent,
  DragEvent,
  memo,
  MouseEvent,
  useState,
} from 'react'

import { useHandler, useUniqId } from '@surglogs-app/hooks'
import { Box } from '@surglogs-app/ui'

import { FileTypeGroup } from '../constants'
import { getAcceptAttrFromExtensions } from '../services'
import {
  FileInputProps,
  MultiFileInputProps,
  SingleFileInputProps,
} from '../types'

import { MultiFileInputContent } from './MultiFileInputContent'
import { SingleFileInputContent } from './SingleFileInputContent'

const stopEvent = (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
  return false
}

export const FileInput = memo(
  <T extends SingleFileInputProps | MultiFileInputProps>(
    props: FileInputProps<T>,
  ) => {
    const [dragging, setDraggingValue] = useState(props.dragging ?? false)

    const handleDragEnter = useHandler((e) => {
      setDraggingValue(true)
      stopEvent(e)
    })

    const handleDragLeave = useHandler((e) => {
      setDraggingValue(false)
      stopEvent(e)
    })

    const onDrop = useHandler(
      (e: DragEvent<HTMLInputElement | HTMLSpanElement>) => {
        e.preventDefault()
        e.stopPropagation()

        if (typeof props.onChange === 'function') {
          const files = props.multiple
            ? e.dataTransfer.files
            : ([e.dataTransfer.files[0]] as unknown as FileList)

          // DragEvent to ChangeEvent type conversion
          const event = e as unknown as ChangeEvent<HTMLInputElement>
          event.target = {
            files,
          } as EventTarget & HTMLInputElement

          props.onChange(event)
        }

        setDraggingValue(false)
      },
    )

    const id = useUniqId({ prefix: 'file-upload' })

    const {
      accept,
      autoAccept = false,
      fileTypesGroup = FileTypeGroup.ALL,
    } = props

    const extensions = autoAccept
      ? getAcceptAttrFromExtensions(fileTypesGroup)
      : undefined

    return (
      <Box {...{ role: 'group', 'aria-label': 'file' }}>
        {props.multiple ? (
          <MultiFileInputContent
            {...{
              ...props,
              accept: accept ?? extensions,
              onDragEnter: handleDragEnter,
              onDragOver: stopEvent,
              onDragLeave: handleDragLeave,
              dragging,
              onDrop,
              id,
            }}
          />
        ) : (
          <SingleFileInputContent
            {...{
              ...props,
              accept: accept ?? extensions,
              onDragEnter: handleDragEnter,
              onDragOver: stopEvent,
              onDragLeave: handleDragLeave,
              dragging,
              onDrop,
              id,
            }}
          />
        )}
      </Box>
    )
  },
)
